import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Box, CircularProgress, Typography} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AzureAuthService from '../services/AzureAuthService';
import { AzureAuthContextType, AzureAuthData } from '../types/Azure';
import { getRuntimeConfig } from '../runtime/runtimeConfig';

const AzureAuthContext = createContext<AzureAuthContextType | undefined>(undefined);

interface AzureAuthProviderProps {
  children: ReactNode;
}

export const AzureAuthProvider: React.FC<AzureAuthProviderProps> = ({ children }) => {
  const { t } = useTranslation(['errors', 'common']);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [azureAuthData, setAzureAuthData] = useState<AzureAuthData | undefined>(undefined);
  
  const checkAzureAuth = async () => {
    // /runtime-config knows whether this origin has the Microsoft binding.
    // `false` skips the probe (and the full-screen spinner) entirely; `true`
    // or unknown (older backend) probes as before, which also performs the
    // token exchange when a platform session exists.
    if (getRuntimeConfig().auth.azure_enabled === false) {
      setIsEnabled(false);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    
    try {
      const azureAuthData = await AzureAuthService.checkAzureAuth();
      setIsEnabled(azureAuthData?.is_enabled ?? false);
      setAzureAuthData(azureAuthData);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial check on mount
  useEffect(() => {
    checkAzureAuth();
  },[azureAuthData]); // eslint-disable-line react-hooks/exhaustive-deps

  const logoutFromAzure = useCallback((): void => {
    AzureAuthService.logoutFromMicrosoft();
    setAzureAuthData(undefined);
    setIsEnabled(false);
  }, []);

  const value: AzureAuthContextType = {
    isLoading,
    isEnabled,
    logoutFromAzure
  };

  // Show loading screen while checking Azure authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          {t('buttons.loading', { ns: 'common' })}
        </Typography>
      </Box>
    );
  }

  return (
    <AzureAuthContext.Provider value={value}>
      {children}
    </AzureAuthContext.Provider>
  );
};

export const useAzureAuth = (): AzureAuthContextType => {
  const context = useContext(AzureAuthContext);
  if (context === undefined) {
    throw new Error('useAzureAuth must be used within a AzureAuthProvider');
  }
  return context;
};

export default AzureAuthContext;